import { useEffect, useState, useContext } from "react";
import * as _ from "lodash";

import "./EUMRVVoyages.scss";

import CustomTable from "../../../../../../common/customTable/CustomTable";
import {
	EUMRV_VOYAGES_HEADERS,
	EUMRVVoyageColumnsData,
} from "../../../../../../constants/CustomTableHeaders";
import { useSelector } from "react-redux";
import { ClaimsContext } from "../../../../../../services/ClaimsService";
import { ClaimType } from "../../../../../../constants/claims";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { roundOff } from "../../../../../../services/utilities";
import { FeatureType } from "../../../../../../constants/FeatureToggles";

const EmissionsReported = ({ data, loading }) => {
	const { userHasClaim } = useContext(ClaimsContext);
	const [columnData, setColumndata] = useState(EUMRVVoyageColumnsData);
	const [showDirectMeasurements, setShowDirectMeasurements] = useState(data?.some(item => item.showDirectMeasurements));

	const user = useSelector((state) => state.userInfo.userData);

	const alwaysColumns = EUMRVVoyageColumnsData?.filter(
		(column) => !column.condition
	);
	const conditionalColumns = EUMRVVoyageColumnsData?.filter(
		(column) => column.condition && userHasClaim(column.condition)
	);

	const staticColumns = userHasClaim(ClaimType.VIEW_ABS_INTERNAL_COLUMNS)
		? [
			...alwaysColumns.slice(0, 8),
			...(conditionalColumns?.filter(
				(item) => item.accessorKey === "calcTimeAtSea"
			) || []),
			...alwaysColumns.slice(8, 10),
			...conditionalColumns?.filter(
				(item) => item.accessorKey === "calcDistanceTravelled"
			),
			...alwaysColumns.slice(10),
		]
		: [
			...alwaysColumns.slice(0, 8),
			...alwaysColumns.slice(9, 11),
			...alwaysColumns.slice(12),
		];

	// Refine the data by adding a "DieselGasOilCons" entry if not present
	useEffect(() => {
		if (
			data &&
			data[0]?.voyageConsumptions.some((fuel) =>
				["MDO", "MGO"].includes(fuel.fuelName)
			)
		) {
			data?.forEach((entry) => {
				if (
					entry?.voyageConsumptions.every(
						(fuel) => fuel.fuelName !== "DieselGasOilCons"
					)
				) {
					entry.voyageConsumptions.unshift({
						fuelName: "DieselGasOilCons",
						consumption: entry.dieselGasOilCons,
					});
				}
			});
		}
	}, [data]);

	// method to filter dynamic columns based on non-zero values
	const getFilteredColumns = (data, columnType, columns) => {
		const nonZeroNames = new Set();

        // Collect non-zero fuel names based on the type (consumption, MNC, transport work)
        data?.forEach((item) => {
            item[columnType]?.forEach((entry) => {
                if (
                    columnType === "directCo2" &&
                    ((entry.reportedValue !== 0 &&
					entry.reportedValue !== 0.00 &&
                    entry.reportedValue !== null &&
                    entry.reportedValue !== undefined) ||
					(entry.estimatedValue !== 0 &&
                    entry.estimatedValue !== 0.00 &&
                    entry.estimatedValue !== null &&
                    entry.estimatedValue !== undefined))
                ) {
                    nonZeroNames.add(entry?.reportedKey);
					nonZeroNames.add(entry?.estimatedKey);
                }
                if (
                    columnType === "voyageConsumptions" &&
                    entry.consumption !== 0 &&
                    entry.consumption !== null &&
                    entry.consumption !== undefined
                ) {
                    nonZeroNames.add(entry?.fuelName);
                }
                if (
                    columnType === "voyageMncs" &&
                    entry.mnc !== 0 &&
                    entry.mnc !== null &&
                    entry.mnc !== undefined
                ) {
                    nonZeroNames.add(entry?.fuelName);
                }
                if (
                    columnType === "transportWorks" &&
                    entry.value !== 0 &&
                    entry.value !== null &&
                    entry.value !== undefined
                ) {
                    nonZeroNames.add(entry?.transportWorkType);
                }
            });
        });

		// Filter columns based on non-zero values
		return columns.filter((column) => {
			const name = column.accessorKey.split("_")[1];
			return nonZeroNames.has(name);
		});
	};

	// method to filter static columns based on non-zero values
	const getFilteredStaticColumns = (objArray, columnsArr) => {
		const nonZeroHeaders = new Set();
		objArray?.forEach((obj) => {
			Object?.entries(obj)?.forEach(([key, value]) => {
				if (value !== 0 && value !== null && value !== undefined) {
					nonZeroHeaders.add(key);
				}
			});
		});
		return columnsArr.filter((column) =>
			nonZeroHeaders.has(column.accessorKey)
		);
	};

	const GetPossibleKeys = (allData, dataType, keyName) => {
		return _.chain(allData).flatMap(dataType).map(keyName).uniq().value();
	};

    const GetCo2Keys = (allData, dataType) => {
        return _.chain(allData)
            .flatMap(dataType) // Flatten all 'directCo2' arrays
            .flatMap(item => [item.reportedKey, item.estimatedKey]) // Extract both keys
            .uniq()
            .value();
    };

    // Initialize dynamic columns for consumption, MNC, and transport work
    const getDynamicColumns = (data) => {
        const dynamicColumns = [];
        const dynamicMncColumns = [];
        const dynamicTransportWork = [];

        if(showDirectMeasurements) {
            if (Array.isArray(data[0]?.directCo2)) {
            const possibleKeys = GetCo2Keys(data, "directCo2");

            possibleKeys.forEach((item) => {
                dynamicColumns.push({
                    id: `directCo2_${item}`,
                    accessorKey: `directCo2_${item}`,
                    header: `${
                        item === "DieselGasOilCons"
                            ? "Diesel/Gas Oil Cons. (MT)"
                            :  `${item}. (MT)`
                    }`,
                    Cell: ({ row }) => {
                        const directCo2Entry = row.original.directCo2.find(
                            (fuel) => fuel.reportedKey === item || fuel.estimatedKey === item
                        );
                        const directCo2Value= directCo2Entry ? (item.includes('Estimated')? directCo2Entry.estimatedValue || 0 :directCo2Entry.reportedValue||0) : 0;

                        return <div className="d-inline-flex" title={directCo2Value}>
                            {roundOff(directCo2Value, 2, 2)}
                            {directCo2Entry?.percentage!=='0.00' && item.includes('Estimated') && <div className="ml-2" style={{color:directCo2Entry?.isPositive?'red':'green'}}>{directCo2Entry?.isPositive?<ArrowUpwardIcon fontSize="small"/>:<ArrowDownwardIcon fontSize="small"/>}
                            <b>{directCo2Entry?.percentage}%</b></div>}
                        </div>
                    },
                });
            });
        }
        }
        else {
            if (Array.isArray(data[0]?.voyageConsumptions)) {
            const possibleKeys = GetPossibleKeys(
                data,
                "voyageConsumptions",
                "fuelName"
            );
            possibleKeys.forEach((item) => {
                dynamicColumns.push({
                    id: `voyageConsumptions_${item}`,
                    accessorKey: `voyageConsumptions_${item}`,
                    header: `${
                        item === "DieselGasOilCons"
                            ? "Diesel/Gas Oil Cons. (MT)"
                            : `${item} Cons. (MT)`
                    }`,
                    Cell: ({ row }) =>
                        row.original.voyageConsumptions.find(
                            (fuel) => fuel.fuelName === item
                        )?.consumption || 0,
                });
            });
        }
        }

		if (Array.isArray(data[0]?.voyageMncs)) {
			const possibleKeys = GetPossibleKeys(
				data,
				"voyageMncs",
				"fuelName"
			);
			possibleKeys.forEach((item) => {
				dynamicMncColumns.push({
					id: `voyageMncs_${item}`,
					accessorKey: `voyageMncs_${item}`,
					header: `${item} MNC. (MT)`,
					Cell: ({ row }) =>
						row.original.voyageMncs.find(
							(fuel) => fuel.fuelName === item
						)?.mnc || 0,
				});
			});
		}

		if (Array.isArray(data[0]?.transportWorks)) {
			const possibleKeys = GetPossibleKeys(
				data,
				"transportWorks",
				"transportWorkType"
			);
			possibleKeys.forEach((item) => {
				dynamicTransportWork.push({
					id: `transportWorks_${item}`,
					accessorKey: `transportWorks_${item}`,
					header: `Transport Work (${item}*nm)`,
					Cell: ({ row }) =>
						row.original.transportWorks.find(
							(work) => work.transportWorkType === item
						)?.value || 0,
				});
			});
		}

		// Return filtered dynamic columns based on data content
		return {
			dynamicColumns: getFilteredColumns(
				data,
				showDirectMeasurements? "directCo2" : "voyageConsumptions",
				dynamicColumns
			),
			dynamicMncColumns: getFilteredColumns(
				data,
				"voyageMncs",
				dynamicMncColumns
			),
			dynamicTransportWork: getFilteredColumns(
				data,
				"transportWorks",
				dynamicTransportWork
			),
		};
	};

	// Use useEffect to update column data only when the data or loading changes
	useEffect(() => {
		if (loading) {
			setColumndata(staticColumns);
		} else {
			setColumndata([]);
			if (data && data?.length > 0) {
				const {
					dynamicColumns,
					dynamicMncColumns,
					dynamicTransportWork,
				} = getDynamicColumns(data);
				const dynamicEUMRVVoyageColumnsData =
					dynamicColumns || dynamicMncColumns || dynamicTransportWork
						? userHasClaim(ClaimType.VIEW_ABS_INTERNAL_COLUMNS)
							? [
								...staticColumns.slice(0, 1),
								...EUMRVVoyageColumnsData?.slice(1, 4),
								...getFilteredStaticColumns(
									data,
									EUMRVVoyageColumnsData?.slice(4, 19)
								),
								...dynamicColumns,
								...dynamicMncColumns,
								...getFilteredStaticColumns(
									data,
									EUMRVVoyageColumnsData?.slice(19, 24)
								),
								//feature toggle for blend fuel registry
								...(
									 getFilteredStaticColumns(
										data,
										EUMRVVoyageColumnsData?.slice(24, 28)
									)
									
									),
								...dynamicTransportWork,
								...getFilteredStaticColumns(
									data,
									EUMRVVoyageColumnsData?.slice(28)
								),
							]
							: [
								...staticColumns.slice(0, 1),
								...EUMRVVoyageColumnsData?.slice(1, 4),
								...getFilteredStaticColumns(
									data,
									EUMRVVoyageColumnsData?.slice(4, 8)
								),
								...getFilteredStaticColumns(
									data,
									EUMRVVoyageColumnsData?.slice(9, 11)
								),
								...getFilteredStaticColumns(
									data,
									EUMRVVoyageColumnsData?.slice(12, 19)
								),
								...dynamicColumns,
								...dynamicMncColumns,
								...getFilteredStaticColumns(
									data,
									EUMRVVoyageColumnsData?.slice(19, 24)
								),
								//feature toggle for blend fuel registry
								...(
									 getFilteredStaticColumns(
										data,
										EUMRVVoyageColumnsData?.slice(24, 28)
									)
									
									),
								...dynamicTransportWork,
								...getFilteredStaticColumns(
									data,
									EUMRVVoyageColumnsData?.slice(28)
								),
							]
						: [
							...EUMRVVoyageColumnsData?.slice(0, 4),
							...getFilteredStaticColumns(data, staticColumns.slice(4)),
						];
				setColumndata(dynamicEUMRVVoyageColumnsData);
			} else {
				setColumndata(staticColumns);
			}
		}
	}, [data, loading]);

	// Return the component rendering the table
	return (
		<div className="EUMRV-custom-table-container emission-reported-voyage eu_mrv_voyage">
			<CustomTable
				columnsData={columnData}
				renderTableData={data || []}
				customProps={{
					isLoading: loading,
					header: EUMRV_VOYAGES_HEADERS.EMISSION_REPORT,
					tableName: "emissions-report",
					maxHeight: "30vh",
					enableSort: false,
					columnOrder: columnData.map((column) => column.accessorKey),
				}}
			/>
		</div>
	);
};

export default EmissionsReported;
